import React from 'react';
import { t } from '../../../utils/textUtils';
import {
  Box,
  Grid,
  Typography,
  Divider,
  Paper,
  Chip,
  Stack
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CategoryIcon from '@mui/icons-material/Category';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentIcon from '@mui/icons-material/Payment';
import NotesIcon from '@mui/icons-material/Notes';

const ReviewForm = ({ formData, users = [], dropdownData = {} }) => {
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Get user name by ID (for project managers, site managers, site engineers)
  const getUserName = (userId) => {
    if (!userId) return '-';
    
    // Convert userId to number for comparison
    const numericUserId = parseInt(userId);
    
    // Try to find in specific user types from dropdownData first
    const allUserTypes = [
      ...(dropdownData.projectManagers || []),
      ...(dropdownData.siteManagers || []),
      ...(dropdownData.siteEngineers || [])
    ];
    
    const user = allUserTypes.find(u => 
      u.id === numericUserId || 
      u.id === userId || 
      u.id === String(userId)
    );
    
    if (user) {
      return user.fullName || user.username || `User ${userId}`;
    }
    
    // Fallback to general users array
    if (!users || !Array.isArray(users)) {
      return `User ${userId}`;
    }
    
    const fallbackUser = users.find(u => 
      u.id === numericUserId || 
      u.id === userId || 
      u.id === String(userId)
    );
    
    if (fallbackUser) {
      return fallbackUser.fullName || fallbackUser.username || fallbackUser.name || `User ${userId}`;
    }
    
    // Additional debug information
    return `User ${userId}`;
  };

  // Specific function for client
  const getClientName = (clientId) => {
    if (!clientId) return '-';
    
    // Use clients from dropdownData if available
    const clients = dropdownData.clients || [];
    
    const client = clients.find(c => 
      c.id === parseInt(clientId) || 
      c.id === clientId || 
      c.id === String(clientId)
    );
    
    if (client) {
      return client.name;
    }
    
    // Fallback to general getUserName
    return getUserName(clientId);
  };

  // Specific function for production coordinator
  const getProductionCoordinatorName = (coordinatorId) => {
    if (!coordinatorId) return '-';
    
    // Use production coordinators from dropdownData if available
    const productionCoordinators = dropdownData.productionCoordinators || [];
    
    const coordinator = productionCoordinators.find(coord => 
      coord.id === parseInt(coordinatorId) || 
      coord.id === coordinatorId || 
      coord.id === String(coordinatorId)
    );
    
    if (coordinator) {
      return coordinator.name;
    }
    
    // Fallback to general getUserName
    return getUserName(coordinatorId);
  };
  
  const renderValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return value;
  };

  const InfoRow = ({ label, value, icon }) => (
    <Box
      className="info-row"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '180px minmax(0, 1fr)' },
        gap: 2,
        py: 1.2,
        borderBottom: '1px dashed',
        borderColor: 'divider',
        alignItems: 'start'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', minWidth: 0 }}>
        {icon}
        <Typography variant="caption" sx={{ letterSpacing: 0.4, textTransform: 'uppercase' }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {typeof value === 'string' || typeof value === 'number'
          ? (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                whiteSpace: 'pre-wrap'
              }}
            >
              {renderValue(value)}
            </Typography>
          )
          : (value || <Typography variant="body2" sx={{ fontWeight: 600 }}>-</Typography>)}
      </Box>
    </Box>
  );

  const SectionCard = ({ title, subtitle, icon, children, tone, accent }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2,
        borderColor: 'divider',
        bgcolor: tone || '#ffffff',
        borderTop: accent ? `4px solid ${accent}` : '4px solid #e2e8f0',
        boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '12px',
            bgcolor: accent ? `${accent}1A` : 'rgba(63,81,181,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ '& .info-row:last-of-type': { borderBottom: 'none' } }}>
        {children}
      </Box>
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: '#1f2937', fontWeight: 800 }}>
        {t('REVIEW INFORMASI PROYEK')}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph sx={{ maxWidth: 720 }}>
        {t('PERIKSA KEMBALI SEMUA INFORMASI PROYEK SEBELUM MEMBUAT PROYEK BARU.')}
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2.5,
          borderColor: 'divider',
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography variant="overline" color="text.secondary">
              {t('RINGKASAN PROYEK')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>
              {formData.name || '-'}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
              <Chip label={`${t('KODE')}: ${formData.kodeProyek || '-'}`} size="small" variant="outlined" />
              <Chip label={`${t('JENIS')}: ${formData.jenisProyek || '-'}`} size="small" variant="outlined" />
              <Chip label={`${t('SEKTOR')}: ${formData.sektor || '-'}`} size="small" variant="outlined" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {formData.location || '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">{t('STATUS AWAL')}</Typography>
                <Chip label={t('PERENCANAAN')} color="warning" size="small" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">{t('MULAI')}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(formData.startDate)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">{t('TARGET SELESAI')}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(formData.estimatedEndDate)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">{t('DURASI')}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formData.startDate && formData.estimatedEndDate
                    ? `${Math.ceil((new Date(formData.estimatedEndDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24))} ${t('HARI')}`
                    : '-'}
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
          gap: 3,
          alignItems: 'start',
          alignContent: 'start',
          gridAutoRows: 'auto'
        }}
      >
        <SectionCard
          title={t('INFORMASI UMUM PROYEK')}
          subtitle={t('Identitas dasar proyek dan lokasi')}
          icon={<AssignmentIndIcon fontSize="small" />}
          tone="#F8FAFC"
          accent="#64748B"
        >
          <InfoRow label={t('NAMA PROYEK')} value={formData.name} icon={<AssignmentIndIcon fontSize="small" />} />
          <InfoRow label={t('KODE PROYEK')} value={formData.kodeProyek} icon={<QrCodeIcon fontSize="small" />} />
          <InfoRow label={t('NO PO')} value={formData.noPO} icon={<ReceiptLongIcon fontSize="small" />} />
          <InfoRow label={t('LOKASI PROYEK')} value={formData.location} icon={<LocationOnIcon fontSize="small" />} />
          <InfoRow
            label={t('KOORDINAT LOKASI')}
            value={(formData.latitude && formData.longitude) ? `${formData.latitude}, ${formData.longitude}` : '-'}
            icon={<LocationOnIcon fontSize="small" />}
          />
          <InfoRow label={t('JENIS PROYEK')} value={formData.jenisProyek} icon={<HomeWorkIcon fontSize="small" />} />
          <InfoRow label={t('DESKRIPSI')} value={formData.description} icon={<DescriptionIcon fontSize="small" />} />
        </SectionCard>

        <SectionCard
          title={t('WAKTU DAN JADWAL')}
          subtitle={t('Rentang dan durasi pekerjaan')}
          icon={<CalendarTodayIcon fontSize="small" />}
          tone="#F5F3FF"
          accent="#8B5CF6"
        >
          <InfoRow label={t('TANGGAL MULAI')} value={formatDate(formData.startDate)} icon={<CalendarTodayIcon fontSize="small" />} />
          <InfoRow label={t('TANGGAL TARGET SELESAI')} value={formatDate(formData.estimatedEndDate)} icon={<CalendarTodayIcon fontSize="small" />} />
          <InfoRow
            label={t('DURASI PROYEK')}
            value={
              formData.startDate && formData.estimatedEndDate
                ? `${Math.ceil((new Date(formData.estimatedEndDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24))} ${t('HARI')}`
                : '-'
            }
            icon={<AccessTimeIcon fontSize="small" />}
          />
        </SectionCard>

        <SectionCard
          title={t('RENCANA & VOLUME PEKERJAAN')}
          subtitle={t('Ruang lingkup dan ukuran pekerjaan')}
          icon={<ApartmentIcon fontSize="small" />}
          tone="#F0FDF4"
          accent="#22C55E"
        >
          <InfoRow label={t('JUMLAH UNIT BANGUNAN')} value={formData.jumlahUnitBangunan || formData.jumlahUnitModular || '-'} icon={<ApartmentIcon fontSize="small" />} />
          <InfoRow label={t('TIPE BANGUNAN')} value={formData.tipeBangunan || formData.tipePrefabrikasi || '-'} icon={<CategoryIcon fontSize="small" />} />
          <InfoRow
            label={t('LUAS TOTAL PROYEK')}
            value={`${formData.luasTotalLahan || '-'} m² (LAHAN) / ${formData.luasTotalBangunan || '-'} m² (BANGUNAN)`}
            icon={<SquareFootIcon fontSize="small" />}
          />
          <InfoRow
            label={t('LAYOUT DESAIN')}
            value={(formData.layoutDesainFile && formData.layoutDesainFile.name) ? formData.layoutDesainFile.name : t('TIDAK ADA FILE')}
            icon={<InsertDriveFileIcon fontSize="small" />}
          />
        </SectionCard>

        <SectionCard
          title={t('BIAYA DAN PENDANAAN')}
          subtitle={t('Ringkasan finansial dan skema pembayaran')}
          icon={<AccountBalanceIcon fontSize="small" />}
          tone="#FFF7ED"
          accent="#F59E0B"
        >
          <InfoRow label={t('NILAI PROYEK')} value={formatCurrency(formData.totalAnggaran)} icon={<AccountBalanceIcon fontSize="small" />} />
          <InfoRow label={t('SUMBER DANA')} value={formData.sumberDana} icon={<AccountBalanceIcon fontSize="small" />} />
          <InfoRow label={t('RENCANA PEMBAYARAN')} value={formData.rencanaPembayaran} icon={<PaymentIcon fontSize="small" />} />
          {formData.detailPembayaran && (
            <InfoRow label={t('DETAIL PEMBAYARAN')} value={formData.detailPembayaran} icon={<NotesIcon fontSize="small" />} />
          )}
        </SectionCard>

        <SectionCard
          title={t('TIM PELAKSANA')}
          subtitle={t('Pihak yang bertanggung jawab')}
          icon={<PersonIcon fontSize="small" />}
          tone="#EFF6FF"
          accent="#3B82F6"
        >
          <InfoRow
            label={t('KLIEN')}
            value={formData.projectClient
              ? (typeof formData.projectClient === 'object' ? formData.projectClient.name : formData.projectClient)
              : getClientName(formData.projectClientId)}
            icon={<PersonIcon fontSize="small" />}
          />
          <InfoRow label={t('MANAJER PROYEK')} value={getUserName(formData.managerId)} icon={<PersonIcon fontSize="small" />} />
          <InfoRow
            label={t('KOORDINATOR PRODUKSI PABRIK')}
            value={formData.productionCoordinator
              ? (typeof formData.productionCoordinator === 'object' ? formData.productionCoordinator.name : formData.productionCoordinator)
              : getProductionCoordinatorName(formData.productionCoordinatorId)}
            icon={<PersonIcon fontSize="small" />}
          />
          <InfoRow label={t('MANAJER LAPANGAN')} value={getUserName(formData.siteManagerId)} icon={<PersonIcon fontSize="small" />} />
          <InfoRow
            label={t('INSINYUR LAPANGAN')}
            value={Array.isArray(formData.siteEngineerIds) && formData.siteEngineerIds.length > 0
              ? (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {formData.siteEngineerIds.map((id) => (
                    <Chip key={id} size="small" label={getUserName(id)} />
                  ))}
                </Stack>
              )
              : getUserName(formData.siteEngineerId)}
            icon={<PersonIcon fontSize="small" />}
          />
        </SectionCard>
      </Box>
    </Box>
  );
};

export default ReviewForm;
